// The candidate list: the /api/candidates head window plus the rows Load more
// appended below it. Pure, since StrictMode double-invokes the updaters built on it.
import type { Candidate, CandidatePage, CandidatePageQuery } from './api';

export type CandidateList = {
  /** The newest window, as the last /api/candidates response had it. */
  head: Candidate[];
  /** Rows Load more appended below the head; a poll never refetches them. */
  older: Candidate[];
  /** Whether the head came from the mainline (first-parent) walk rather than the
   * REST/compare fallback. Every row in the list shares that one ordering. */
  mainline: boolean;
  /** The last page's hasMore, and the prod cut it was asked to stop at. */
  lastPage?: { hasMore: boolean; stopAt?: string };
};

export const EMPTY_LIST: CandidateList = { head: [], older: [], mainline: false };

/** Displayed order: the head, then the older rows it does not already hold. */
export function mergeRows({ head, older }: CandidateList): Candidate[] {
  if (!older.length) return head;
  const inHead = new Set(head.map(c => c.sha));
  return [...head, ...older.filter(c => !inHead.has(c.sha))];
}

/** A polled head. Rows main pushed out of it move to the front of the older rows so no gap
 * opens. History jumping (no shared row), a tip moved back or a listing mode change starts over. */
export function replaceHead(list: CandidateList, head: Candidate[], mainline: boolean): CandidateList {
  const inHead = new Set(head.map(c => c.sha));
  // An older cached snapshot, served while GitHub rate limits, can put the tip back down the old head.
  const tipMovedBack = list.head.findIndex(c => c.sha === head[0]?.sha) > 0;
  if (mainline !== list.mainline || tipMovedBack || !list.head.some(c => inHead.has(c.sha))) return { head, older: [], mainline };
  if (!list.older.length) return { ...list, head };
  const pushedOut = list.head.filter(c => !inHead.has(c.sha));
  return { ...list, head, older: [...pushedOut, ...list.older].filter(c => !inHead.has(c.sha)) };
}

/** A Load more page, dropped when the list no longer ends at the row it was fetched
 * after or no longer lists in its mode (a poll reset or reshaped it while it loaded). */
export function appendPage(
  list: CandidateList,
  query: Pick<CandidatePageQuery, 'after' | 'stopAt' | 'mainline'>,
  page: CandidatePage,
): CandidateList {
  const rows = mergeRows(list);
  if (query.mainline !== list.mainline || rows[rows.length - 1]?.sha !== query.after) return list;
  const seen = new Set(rows.map(c => c.sha));
  const fresh: Candidate[] = [];
  for (const c of page.candidates) {
    if (seen.has(c.sha)) continue;
    seen.add(c.sha);
    fresh.push(c);
  }
  return { ...list, older: [...list.older, ...fresh], lastPage: { hasMore: page.hasMore, stopAt: query.stopAt } };
}

/** Unset until a page loads against this prod cut, so Load more comes back once
 * the cut moves. */
export function lastPageHasMore({ lastPage }: CandidateList, stopAt?: string): boolean | undefined {
  return lastPage && lastPage.stopAt === stopAt ? lastPage.hasMore : undefined;
}
